<template>
  <BaseModal
    :title="
      product.status === 'preOrder'
        ? _T('@Product pre-order')
        : _T('@Report availability')
    "
    @on-close-modal="onCloseModal"
  >
    <div class="modal-preorder__body">
      <div class="modal-preorder__select-item">
        <div class="modal-preorder__img">
          <img
            :src="product.getMainImage('small')"
            :alt="cleanStringsConcat([product.getName(), 'image'])"
          />
        </div>
        <div class="modal-preorder__item-info">
          <p class="modal-preorder__item-name">
            {{ product.getName() }}
          </p>
          <p class="modal-preorder__item-code">
            {{ `${_T("@Code")} ${product.code}` }}
          </p>
        </div>
      </div>
      <MyForm
        id="product-preorder"
        name="product-preorder"
        :schema="schema"
        :state="form"
        class="modal-preorder__form"
        @submit="onSubmit"
      >
        <MyInput
          v-model.trim="form.name"
          name="name"
          caption="@Name"
          autocomplete="given-name"
        />
        <MyInput
          v-model.trim="form.phone"
          v-maska="'+38(0##) ### ## ##'"
          name="phone"
          type="tel"
          autocomplete="phone"
          caption="@Phone number"
        />
        <MyInput
          v-model.trim="form.email"
          name="email"
          type="email"
          autocomplete="email"
          caption="E-mail"
        />
        <div class="modal-preorder__checkbox-wrp">
          <MyCheckbox
            v-model="form.isAgreementAccepted"
            name="isAgreementAccepted"
            color="var(--color-blue-700)"
          >
            <span>
              {{ _T("@I agree with") }}
              <MyLink name="delivery" target="_blank">
                {{ _T("@terms of delivery") }}
              </MyLink>
              {{ _T("@and") }}
              <MyLink name="privacy-policy" target="_blank">
                {{ _T("@privacy policy") }}
              </MyLink>
            </span>
          </MyCheckbox>
        </div>
        <ButtonProductSubscribe
          type="submit"
          :status="product.status"
          :disable="isHandled"
        />
      </MyForm>
    </div>
  </BaseModal>
</template>

<script setup>
import ButtonProductSubscribe from "~/modules/shared/buttons/ButtonProductSubscribe.vue";
import { formatPhone, phoneCheck } from "~~/utils/phoneHelper";
import {
  preOrderModal,
  successfulModal,
  useModalStore,
} from "~~/modules/shared/modals/store/modalStore";
import { useUserStore } from "~/store/userStore";
import { useSingletonHandler } from "~/uses/useSingletonHandler";
import { useAPI } from "~/uses/useMyFetch";
import BaseModal from "~/modules/shared/modals/BaseModal.vue";
import MyForm from "~/nuxt_modules/common/ui/components/form/MyForm.vue";
import MyInput from "~/nuxt_modules/common/ui/components/form/MyInput.vue";
import { vMaska } from "maska/vue";
import MyCheckbox from "~/nuxt_modules/common/ui/components/form/MyCheckbox.vue";
import { z } from "zod";

const modalStore = useModalStore();
const product = storeToRefs(modalStore).getExtra.value(preOrderModal);

const userStore = useUserStore();
const { currentUser: user } = storeToRefs(userStore);

onMounted(() => {
  if (null !== user.value) {
    form.name = user.value.name.firstName;
    form.phone = user.value.phone;
    form.email = user.value.email;
  }
});

const successfulData = {
  title: _T("@Successful modal title"),
  description: _T("@Successful modal text"),
  isWithTimeout: true,
};

const schema = z.object({
  name: z
    .string(_T("@Entry field required"))
    .max(255, `${_T("@Maximum field length")} 255`)
    .regex(/^\S*$/, _T("@Must not contain spaces")),
  phone: z
    .string(_T("@Entry field required"))
    .refine(
      (phone) => phoneCheck(phone),
      _T("@You entered an incorrect phone number"),
    ),
  email: z
    .email(_T("@You entered an incorrect email"))
    .max(254, `${_T("@Maximum field length")} 254`),
  isAgreementAccepted: z.literal(
    true,
    _T("@You must accept the terms and conditions"),
  ),
});

const form = reactive({
  name: undefined,
  phone: undefined,
  email: undefined,
  isAgreementAccepted: true,
});

const { handle: onSubmit, isHandled } = useSingletonHandler(
  async (data) =>
    await useAPI("/catalog/product/subscription/add", {
      method: "POST",
      body: {
        productId: product.id,
        type: product.status === "preOrder" ? "preOrder" : "order",
        email: data.email,
        phone: formatPhone(data.phone),
        name: data.name,
      },
    }).then(() => {
      modalStore.toggleModal(preOrderModal);

      modalStore.toggleModal(successfulModal, successfulData);
    }),
);

const onCloseModal = () => {
  modalStore.toggleModal(preOrderModal);
};
</script>

<style lang="scss" scoped>
.modal-preorder {
  &__body {
    @include flex-container(column, space-between);

    gap: 24px;
  }

  &__select-item {
    @include flex-container(row, space-between, center);

    gap: 16px;
  }

  &__img {
    width: 90px;
    height: 90px;

    flex: 0 0 auto;
  }

  &__item-info {
    @include flex-container(column, space-between);

    gap: 8px;
  }

  &__item-name {
    @include font(16, 22, 600);
    letter-spacing: 0.02em;
    color: #2b2b2b;
  }

  &__item-code {
    @include font(12, 16);
    letter-spacing: 0.02em;
    color: #2b2b2b;
  }

  &__form {
    @include flex-container(column, space-between, center);

    gap: 24px;
  }

  &__checkbox-wrp {
    max-width: 363px;
  }
}
</style>
